import React from "react";
import {Chip, CircularProgress, Typography} from "@material-ui/core";
import FlexBox from "view/components/Layout/FlexBox";
import type {LocalisationController} from "hooks/translation";
import LocalisationTextRow from "./LocalisationTextRow";
import LocalisationRichTextRow, {
  LocalisationRichTextOptions,
} from "./LocalisationRichTextRow";
import useStyles from "./styles";

/**
 * A DOM id safe to hand Quill: `react-quill` turns the toolbar id into a CSS
 * selector (`#<id>`), and the colons in target keys make it invalid and throw.
 */
const toDomId = (value: string) => value.replace(/[^A-Za-z0-9_-]/g, "-");

type Props = {
  controller: LocalisationController;
  targetKey: string;
  onRequestTranslate: (targetKey: string, languageCode?: string) => void;
  richTextOptions?: LocalisationRichTextOptions;
  /** See LocalisationTextRow.readOnly. */
  readOnly?: boolean;
};

const LocalisationFieldGroup = ({
  controller,
  targetKey,
  onRequestTranslate,
  richTextOptions,
  readOnly,
}: Props) => {
  const classes = useStyles();
  const target = controller.getTarget(targetKey);

  if (!target) return null;

  const isRichText = target.kind === "richText";
  const canTranslate = !readOnly && controller.canTranslate(targetKey);

  return (
    <FlexBox column gap={2} fullWidth>
      {target.captionFromSource ? (
        <FlexBox alignCenter gap={1} className={classes.sourceCaption}>
          {target.captionIcon}
          <Typography variant="body2">{target.getSourceText()}</Typography>
        </FlexBox>
      ) : (
        <FlexBox alignCenter gap={1}>
          <Typography variant="body2" className={classes.groupLabel}>
            Translation for <strong>{target.label}</strong>
          </Typography>
          {!!target.labelChip && (
            <Chip label={target.labelChip} className={classes.labelChip} />
          )}
          {target.labelSuffix}
        </FlexBox>
      )}

      {!controller.hasLoaded && (
        <FlexBox alignCenter gap={1}>
          <CircularProgress size={16} />
          <Typography variant="body2">Loading translations…</Typography>
        </FlexBox>
      )}

      {controller.hasLoaded &&
        controller.languages.map((language) =>
          isRichText ? (
            <LocalisationRichTextRow
              key={language.code}
              editorId={toDomId(`localisation-${targetKey}-${language.code}`)}
              language={language}
              value={controller.getValue(targetKey, language.code)}
              revision={controller.getRevision(targetKey, language.code)}
              onChange={(value) =>
                controller.setValue(targetKey, language.code, value)
              }
              onTranslate={() => onRequestTranslate(targetKey, language.code)}
              canTranslate={canTranslate}
              isTranslating={controller.isBusy(targetKey, language.code)}
              options={richTextOptions}
              readOnly={readOnly}
            />
          ) : (
            <LocalisationTextRow
              key={language.code}
              language={language}
              value={controller.getValue(targetKey, language.code)}
              onChange={(value) =>
                controller.setValue(targetKey, language.code, value)
              }
              onTranslate={() => onRequestTranslate(targetKey, language.code)}
              canTranslate={canTranslate}
              isTranslating={controller.isBusy(targetKey, language.code)}
              maxLength={target.maxLength}
              readOnly={readOnly}
            />
          )
        )}
    </FlexBox>
  );
};

export default LocalisationFieldGroup;
